/**
 * Resolves which size_chart key matches the user's profile based on
 * the chart's sizing convention.
 *
 * Charts use one of three conventions:
 * - Letter only:  "S", "M", "L", "XL"
 * - Numeric only: "8", "10", "12"
 * - Mixed:        "M / 10", "L - UK 14", "10 (M)"
 */

import { getKnownConvention, type SizeConvention } from "./retailerSizeConvention";

export type ChartConvention = "letter" | "numeric" | "mixed" | "unknown";

export type SizeResolution =
  | { matched: true; key: string; convention: ChartConvention }
  | {
      matched: false;
      reason: "no_user_size" | "no_chart_match" | "no_chart" | "unknown_convention";
      convention: ChartConvention;
      missing_field?: "letter" | "number";
    };

export type SizeProfile = {
  size_top_letter?: string | null;
  size_top_uk_number?: string | null;
};

const LETTER_PATTERN = /^(X{0,3})(S|M|L)(X*)$/i;
const NUMERIC_PATTERN = /^\d{1,2}$/;
const MIXED_LETTER_FIRST = /^(X{0,3}[SML])\s*[\/\-]\s*(?:UK\s*)?(\d{1,2})/i;
const MIXED_NUMBER_FIRST = /^(?:Size\s*)?(\d{1,2})\s*[\/\-\(]\s*(X{0,3}[SML])/i;

export function detectChartConvention(chartKeys: string[]): ChartConvention {
  if (!chartKeys || chartKeys.length === 0) return "unknown";

  const trimmed = chartKeys.map((k) => k.trim());
  const total = trimmed.length;

  const mixedCount = trimmed.filter(
    (k) => MIXED_LETTER_FIRST.test(k) || MIXED_NUMBER_FIRST.test(k),
  ).length;
  const letterCount = trimmed.filter((k) => LETTER_PATTERN.test(k)).length;
  const numericCount = trimmed.filter((k) => NUMERIC_PATTERN.test(k)).length;

  const half = Math.ceil(total * 0.5);
  if (mixedCount >= half) return "mixed";
  if (letterCount >= half) return "letter";
  if (numericCount >= half) return "numeric";
  return "unknown";
}

export function resolveSizeKey(
  sizeChart: Record<string, unknown> | null | undefined,
  profile: SizeProfile,
): SizeResolution {
  if (!sizeChart || typeof sizeChart !== "object") {
    return { matched: false, reason: "no_chart", convention: "unknown" };
  }
  const chartKeys = Object.keys(sizeChart);
  if (chartKeys.length === 0) {
    return { matched: false, reason: "no_chart", convention: "unknown" };
  }

  const convention = detectChartConvention(chartKeys);

  switch (convention) {
    case "letter": {
      if (!profile.size_top_letter) {
        return {
          matched: false,
          reason: "no_user_size",
          convention,
          missing_field: "letter",
        };
      }
      const userLetter = profile.size_top_letter.trim().toUpperCase();
      const matched = chartKeys.find((k) => k.trim().toUpperCase() === userLetter);
      return matched
        ? { matched: true, key: matched, convention }
        : { matched: false, reason: "no_chart_match", convention };
    }

    case "numeric": {
      if (!profile.size_top_uk_number) {
        return {
          matched: false,
          reason: "no_user_size",
          convention,
          missing_field: "number",
        };
      }
      const userNum = profile.size_top_uk_number.trim();
      const matched = chartKeys.find((k) => k.trim() === userNum);
      return matched
        ? { matched: true, key: matched, convention }
        : { matched: false, reason: "no_chart_match", convention };
    }

    case "mixed": {
      const userLetter = profile.size_top_letter?.trim().toUpperCase() ?? null;
      const userNum = profile.size_top_uk_number?.trim() ?? null;
      if (!userLetter && !userNum) {
        return {
          matched: false,
          reason: "no_user_size",
          convention,
          missing_field: "letter",
        };
      }
      for (const key of chartKeys) {
        const trimmed = key.trim();
        const lf = trimmed.match(MIXED_LETTER_FIRST);
        const nf = trimmed.match(MIXED_NUMBER_FIRST);
        if (lf) {
          const [, kL, kN] = lf;
          if (userLetter && kL.toUpperCase() === userLetter) return { matched: true, key, convention };
          if (userNum && kN === userNum) return { matched: true, key, convention };
        }
        if (nf) {
          const [, kN, kL] = nf;
          if (userNum && kN === userNum) return { matched: true, key, convention };
          if (userLetter && kL.toUpperCase() === userLetter) return { matched: true, key, convention };
        }
      }
      return { matched: false, reason: "no_chart_match", convention };
    }

    default:
      return { matched: false, reason: "unknown_convention", convention: "unknown" };
  }
}

const LETTER_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL"];

/** Sort chart keys by their convention so up/down stepping is well-defined. */
export function sortByChartConvention(keys: string[]): string[] {
  if (keys.length === 0) return [];
  const allNumeric = keys.every((k) => NUMERIC_PATTERN.test(k.trim()));
  if (allNumeric) {
    return [...keys].sort((a, b) => parseInt(a.trim(), 10) - parseInt(b.trim(), 10));
  }
  return [...keys].sort((a, b) => {
    const ai = LETTER_ORDER.indexOf(a.trim().toUpperCase());
    const bi = LETTER_ORDER.indexOf(b.trim().toUpperCase());
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

export function stepSizeKey(
  currentKey: string,
  chartKeys: string[],
  direction: "up" | "down",
): string | null {
  const sorted = sortByChartConvention(chartKeys);
  const idx = sorted.indexOf(currentKey);
  if (idx === -1) return null;
  const targetIdx = direction === "up" ? idx + 1 : idx - 1;
  if (targetIdx < 0 || targetIdx >= sorted.length) return null;
  return sorted[targetIdx];
}

// ---------------------------------------------------------------------------
// Tier-level convention detection (hybrid: real chart data → static map)
// ---------------------------------------------------------------------------

export type TierConventionSource = "chart_data" | "static_map" | "unknown";

export type TierConventionResult = {
  conventions_needed: Array<"letter" | "numeric">;
  sources: Record<string, TierConventionSource>;
};

export type TierLite = {
  retailer_id: string | null;
  size_chart: Record<string, unknown> | null;
};

/**
 * Inspects the size conventions used by the active outfit's tier retailers.
 * Returns which size types the user must provide to enable matching.
 *
 * Priority per tier:
 *   1. Real chart keys from garment_data (truth)
 *   2. Static retailer map (fallback when not scraped)
 *   3. Unknown — does not contribute to the "needed" set
 */
export function detectTierConventions(
  tiers: TierLite[],
): TierConventionResult {
  const conventions: Array<SizeConvention | null> = [];
  const sources: Record<string, TierConventionSource> = {};

  for (const tier of tiers) {
    const key = tier.retailer_id ?? "unknown";
    if (tier.size_chart && Object.keys(tier.size_chart).length > 0) {
      const detected = detectChartConvention(Object.keys(tier.size_chart));
      if (detected !== "unknown") {
        conventions.push(detected);
        sources[key] = "chart_data";
        continue;
      }
    }
    const known = getKnownConvention(tier.retailer_id);
    if (known) {
      conventions.push(known);
      sources[key] = "static_map";
    } else {
      conventions.push(null);
      sources[key] = "unknown";
    }
  }

  const needsLetter = conventions.some((c) => c === "letter" || c === "mixed");
  const needsNumeric = conventions.some((c) => c === "numeric" || c === "mixed");

  const conventions_needed: Array<"letter" | "numeric"> = [];
  if (needsLetter) conventions_needed.push("letter");
  if (needsNumeric) conventions_needed.push("numeric");

  // Edge case: nothing surfaced. Default to letter — more common convention.
  if (conventions_needed.length === 0) {
    conventions_needed.push("letter");
  }

  return { conventions_needed, sources };
}
